import { useContext, useEffect, useRef } from "react";

import { FederationDataContext } from "./context/FederationDataContext";
import { ModuleWrapperContext } from "@Components/ModuleWrapper/ModuleWrapperContext";
import UserSetting from "sccUserSetting";
import PoiOverlay from "sccPoiOverlay";
import GeofenceOverlay from "sccGeofenceOverlay";
import DeviceOverlay from "sccDeviceOverlay";
import Utils from "sccUtils";

export default function FederationData(props) {
  const [fedState] = useContext(FederationDataContext);
  const [mwState, setMwState] = useContext(ModuleWrapperContext);

  const searchField = mwState.searchField;
  const searchTerm = mwState.searchTerm;
  const filteredList = mwState.filteredList;
  const filterSelection = mwState.selectedFilter;

  const checkFederationConnection = fedState.checkFederationConnection;
  const fetchFederationData = fedState.fetchFederationData;
  const removeFederationData = fedState.removeFederationData;

  const fetchIntervalRef = useRef(null);
  const checkIntervalRef = useRef(null);

  useEffect(() => {
    const showFederationDataOnMap = UserSetting.get("map_layers")["Federation"];
    if (showFederationDataOnMap) {
      PoiOverlay.unsetFilter("federationServerId");
      GeofenceOverlay.unsetFilter("federationServerId");
      DeviceOverlay.unsetFilter("federationServerId");
    } else {
      PoiOverlay.setFilter("federationServerId", [undefined]);
      GeofenceOverlay.setFilter("federationServerId", [undefined]);
      DeviceOverlay.setFilter("federationServerId", [undefined]);
    }

    // Function to start checking connection every 10 sec
    const startConnectionCheck = () => {
      if (!checkIntervalRef.current) {
        checkFederationConnection();
        checkIntervalRef.current = setInterval(
          checkFederationConnection,
          10 * 1000
        );
      }
    };

    // Function to start fetching data every 10 sec and check connection every 30 sec
    const startFetchingData = () => {
      if (!fetchIntervalRef.current) {
        fetchFederationData();
        fetchIntervalRef.current = setInterval(fetchFederationData, 60 * 1000);
      }
      if (!checkIntervalRef.current) {
        checkIntervalRef.current = setInterval(
          checkFederationConnection,
          30 * 1000
        );
      }
    };

    // Function to stop all intervals
    const stopAllIntervals = () => {
      if (fetchIntervalRef.current) {
        clearInterval(fetchIntervalRef.current);
        fetchIntervalRef.current = null;
      }
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
    };

    if (
      fedState.federationConnection?.federationServerId &&
      UserSetting.get()?.display_federation_data
    ) {
      stopAllIntervals();
      startFetchingData();
    } else {
      stopAllIntervals();
      removeFederationData();
      startConnectionCheck();
    }

    return () => stopAllIntervals();
  }, [
    fedState.federationConnection?.federationServerId,
    UserSetting.get()?.display_federation_data,
  ]);

  useEffect(() => {
    if (!Utils.getFedKeyNameForModule(mwState.moduleName)) return;

    let updatedModuleData = mwState.moduleLocalData;

    if (fedState.data.length) {
      const fedKey = Utils.getFedKeyNameForModule(mwState.moduleName);
      const fedModuleSpecificData = { ...fedState.data[0][fedKey] };

      // Convert to id-based object
      const newObj = Object.values(fedModuleSpecificData).reduce(
        (acc, item) => {
          acc[item.id] = item;
          return acc;
        },
        {}
      );

      // Merge local + new data
      updatedModuleData = { ...updatedModuleData, ...newObj };
    }

    // Filtering
    const baseFiltered = filteredList(
      Object.values(updatedModuleData),
      filterSelection
    );

    const filteredData =
      searchField && searchTerm
        ? baseFiltered.filter((item) =>
            searchField.some((field) =>
              item[field]?.toLowerCase().includes(searchTerm.toLowerCase())
            )
          )
        : baseFiltered;

    const filteredModuleData = Utils.arrayToObject(filteredData, "id");
    const numberOfItems = filteredModuleData
      ? Object.keys(filteredModuleData).length
      : 0;

    setMwState((p) => ({
      ...p,
      moduleFederationData: fedState.data.length
        ? fedState.data[0][Utils.getFedKeyNameForModule(p.moduleName)]
        : [],
      moduleData: updatedModuleData,
      filteredModuleData: filteredModuleData,
      pagination: {
        ...p.pagination,
        numberOfItems,
        endIndex: Math.min(
          p.pagination.startIndex + p.pagination.itemsPerPage,
          numberOfItems
        ),
      },
    }));
  }, [fedState.version]);

  return null;
}
